import type { AnnotationLayer } from '../render/annotations';
import { Group } from 'three';
import { createTextSprite, disposeTextSprite, type TextSprite } from '../render/textSprite';
import type { PeerPose } from './peers';

export function visitorNamePosition(
  peer: Pick<PeerPose, 'x' | 'y' | 'z' | 'mode'>
): [number, number, number] {
  return [peer.x, peer.y - (peer.mode === 'boat' ? 1.45 : 1.7) + 2.05, peer.z];
}

type NameLabel = TextSprite;

function nameSprite(name: string, layer: AnnotationLayer): NameLabel {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Visitor name rendering requires a canvas context');
  const font = '32px "EB Garamond", Georgia, serif';
  context.font = font;
  canvas.width = Math.ceil(context.measureText(name).width) + 20;
  canvas.height = 56;
  context.font = font;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.lineJoin = 'round';
  context.strokeStyle = '#183426';
  context.lineWidth = 5;
  context.strokeText(name, canvas.width / 2, 28);
  context.fillStyle = '#e8e4d9';
  context.fillText(name, canvas.width / 2, 28);
  const sprite = createTextSprite(canvas, 0.24, layer);
  sprite.name = name;
  return sprite;
}

export function createVisitorNameLabels(show: () => boolean, name: string, layer: AnnotationLayer) {
  const group = new Group();
  group.name = `${name}-names`;
  const labels = new Map<string, NameLabel>();
  return {
    group,
    update(peers: readonly PeerPose[]): void {
      const current = new Set(peers.map((peer) => peer.id));
      for (const [id, sprite] of labels)
        if (!current.has(id)) {
          group.remove(sprite);
          disposeTextSprite(sprite);
          labels.delete(id);
        }
      group.visible = show() && peers.length > 0;
      for (const peer of peers) {
        let sprite = labels.get(peer.id);
        if (!sprite || sprite.name !== peer.name) {
          if (sprite) {
            group.remove(sprite);
            disposeTextSprite(sprite);
          }
          sprite = nameSprite(peer.name, layer);
          labels.set(peer.id, sprite);
          group.add(sprite);
        }
        sprite.position.set(...visitorNamePosition(peer));
      }
    },
    dispose(): void {
      for (const sprite of labels.values()) disposeTextSprite(sprite);
      labels.clear();
      group.clear();
    },
  };
}
